import React from "react";
import { Pencil, PlayCircle, Search, Trash2 } from "lucide-react";
import { AppButton, IconButton, Input } from "../ui";
import { resourceRowMeta } from "./resourceKinds";

/**
 * The resources attached to a session or a topic, one row each.
 *
 * Props:
 *   resources — [{ kind, refId, title, contentType, durationMinutes, addedByTrainer? }]
 *   onEdit(r, i)     — omit to hide Edit (documents have nothing to edit here)
 *   onReplace(r, i)  — omit to hide Replace
 *   onRemove(r, i)
 *   onMinutes(r, i, minutes) — omit to hide the time estimate
 *   empty     — what to say when there is nothing attached
 *   disabled
 */
const ResourceList = ({ resources = [], onEdit, onReplace, onRemove, onMinutes, empty, disabled = false }) => {
  if (!resources.length) {
    return (
      <p className="tw-m-0 tw-rounded-md tw-border tw-border-dashed tw-border-line tw-px-3 tw-py-4 tw-text-center tw-text-[0.8125rem] tw-text-content-muted">
        {empty || "Nothing attached yet."}
      </p>
    );
  }
  return (
    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
      {resources.map((r, i) => {
        const { Icon, label } = resourceRowMeta(r);
        const editable = onEdit && r.refId && r.kind !== "document";
        return (
          <li
            key={`${r.kind}-${r.refId || i}`}
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3 tw-py-2"
          >
            <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-sm tw-bg-surface-sunken tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-content-muted">
              <Icon size={12} aria-hidden="true" /> {label}
            </span>
            <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[0.8125rem] tw-font-medium tw-text-content" title={r.title}>
              {r.title || "Untitled"}
              {r.addedByTrainer && <span className="tw-ms-1.5 tw-text-xs tw-font-normal tw-text-content-subtle">· added by a trainer</span>}
            </span>
            <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
              {onMinutes && (
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  max={600}
                  fullWidth={false}
                  className="tw-w-[4.5rem]"
                  aria-label={`Minutes to complete ${r.title || label}`}
                  title="Minutes to complete — shown to students as a time estimate"
                  value={r.durationMinutes || ""}
                  placeholder="min"
                  disabled={disabled}
                  onChange={(e) => onMinutes(r, i, Number(e.target.value) || 0)}
                />
              )}
              {editable && (
                <AppButton size="xs" variant="outline" onClick={() => onEdit(r, i)} disabled={disabled}>
                  {r.kind === "scorm" ? <PlayCircle size={12} aria-hidden="true" /> : <Pencil size={12} aria-hidden="true" />}
                  {r.kind === "scorm" ? "Preview" : "Edit"}
                </AppButton>
              )}
              {onReplace && (
                <AppButton size="xs" variant="outline" onClick={() => onReplace(r, i)} disabled={disabled}>
                  <Search size={12} aria-hidden="true" /> Replace
                </AppButton>
              )}
              <IconButton
                size="xs"
                icon={Trash2}
                variant="ghost-destructive"
                label={`Remove ${r.title || label}`}
                onClick={() => onRemove(r, i)}
                disabled={disabled}
              />
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default ResourceList;
